import { apiClient, type PageEnvelope } from '@/lib/api-client';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'running'
  | 'paused'
  | 'completed'
  | 'archived';

export interface CampaignStep {
  id: string;
  step_number: number;
  subject_template: string;
  body_html_template: string;
  body_text_template: string | null;
  delay_days_after_prev: number;
  stop_on_reply: boolean;
  attachment_ids: string[];
}

export interface UpdateCampaignStepInput {
  subject_template?: string;
  body_html_template?: string;
  delay_days_after_prev?: number;
  stop_on_reply?: boolean;
  attachment_ids?: string[];
}

export interface CampaignStats {
  total: number;
  sent: number;
  replied: number;
  bounced: number;
  opened: number;
  clicked: number;
}

export interface CampaignSummary {
  id: string;
  name: string;
  status: CampaignStatus;
  lead_list_id: string | null;
  from_mailbox_pool_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  stats: CampaignStats | null;
}

export type ReplyEngine = 'deepseek' | 'mlm';

export interface CampaignDetail extends CampaignSummary {
  send_days: number;
  send_window_start: string;
  send_window_end: string;
  timezone: string;
  scheduled_start_at: string | null;
  min_delay_seconds: number;
  max_delay_seconds: number;
  daily_send_limit: number | null;
  reply_engine: ReplyEngine;
  steps: CampaignStep[];
  stats: CampaignStats;
}

export interface CreateCampaignInput {
  name: string;
  lead_list_id: string;
  from_mailbox_pool_id: string;
  reply_engine?: ReplyEngine;
  send_days?: number;
  send_window_start?: string;
  send_window_end?: string;
  timezone?: string;
  scheduled_start_at?: string | null;
  daily_send_limit?: number | null;
  steps: Array<{
    step_number: number;
    subject_template: string;
    body_html_template: string;
    delay_days_after_prev: number;
    stop_on_reply?: boolean;
    attachment_ids?: string[];
  }>;
}

export interface LaunchResult {
  enqueued: number;
  mailboxes_used: number;
  suppressed_count: number;
}

export interface UpdateCampaignInput {
  name?: string;
  status?: CampaignStatus;
  reply_engine?: ReplyEngine;
  send_days?: number;
  send_window_start?: string;
  send_window_end?: string;
  timezone?: string;
  scheduled_start_at?: string | null;
  min_delay_seconds?: number;
  max_delay_seconds?: number;
  daily_send_limit?: number | null;
}

export const campaignsApi = {
  list: () =>
    apiClient
      .get<PageEnvelope<CampaignSummary>>('/api/v1/campaigns')
      .then((r) => r.items),
  detail: (id: string) => apiClient.get<CampaignDetail>(`/api/v1/campaigns/${id}`),
  create: (input: CreateCampaignInput) =>
    apiClient.post<CampaignSummary>('/api/v1/campaigns', input),
  update: (id: string, input: UpdateCampaignInput) =>
    apiClient.patch<CampaignSummary>(`/api/v1/campaigns/${id}`, input),
  updateStep: (campaignId: string, stepId: string, input: UpdateCampaignStepInput) =>
    apiClient.patch<CampaignStep>(`/api/v1/campaigns/${campaignId}/steps/${stepId}`, input),
  launch: (id: string) =>
    apiClient.post<LaunchResult>(`/api/v1/campaigns/${id}/launch`),
  pause: (id: string) => apiClient.post<CampaignSummary>(`/api/v1/campaigns/${id}/pause`),
  reschedule: (id: string) =>
    apiClient.post<{ updated: number; first_send_at: string | null; last_send_at: string | null }>(
      `/api/v1/campaigns/${id}/reschedule`,
    ),
};
